import dedent from 'dedent';
import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'enable-functions-cms-access' as const;

// No input: the site always comes from the session.
const argumentZod = z.object({});

export type EnableFunctionsCmsAccessArgumentType = z.infer<typeof argumentZod>;

export const enableFunctionsCmsAccessManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Turn on Code Components CMS access',
  prettyStatus: {
    executing: 'Turning on Code Components CMS access',
    executed: 'Enabled Code Components CMS access',
    failed: 'Could not turn on Code Components CMS access',
    partial: 'Requested Code Components CMS access',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Lets this site's Code Components read the site's CMS through \`ctx.cms\`, read-only, both in the Designer preview and on the published site. Nobody sees a credential: the platform adds it outside the component's code.

      Only call this tool after check-functions-cms-access returned \`canTurnOn: true\` and the user agreed, in this conversation, that their Code Components may read this site's CMS content. Use ask-user-question when it is available to ask them. Do not call it without their agreement. When you talk to the user, call it "Code Components CMS access".

      The result has \`available\`:
      - \`true\`: CMS access is on for this site. Code Components can use \`ctx.cms\`.
      - \`false\`: CMS access is off. \`reason\` and \`instructions\` say why and what to tell the user. Do not call this tool again in this conversation unless the user says the situation changed.
    `,
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.CODE_FUNCTIONS,
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  // The Designer has no approval UI for server tools, and MCP tools are never
  // gated, so the description asks the agent to get consent instead.
  confirmBeforeExecuting: false,
});
